import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {createRoot} from 'react-dom/client';
import {Bubble, Prompts, Sender} from '@ant-design/x';
import {
    BulbOutlined,
    CloseOutlined,
    ColumnWidthOutlined,
    CommentOutlined,
    ControlOutlined,
    CopyOutlined,
    DeleteOutlined,
    DeploymentUnitOutlined,
    MinusOutlined,
    ProductOutlined,
    QuestionCircleOutlined,
    SafetyCertificateOutlined,
    SearchOutlined,
    ThunderboltOutlined,
} from '@ant-design/icons';
import {App, Avatar, Button, ConfigProvider, Tooltip} from 'antd';
import {marked} from 'marked';
import DOMPurify from 'dompurify';

const makeSessionId = () => {
    const timePart = Date.now().toString().slice(-9);
    const randomPart = Math.floor(Math.random() * 900000).toString().padStart(6, '0');
    return `${timePart}${randomPart}`;
};

const getSessionId = () => {
    let sessionId = localStorage.getItem('session_id');
    if (!sessionId) {
        sessionId = makeSessionId();
        localStorage.setItem('session_id', sessionId);
    }
    return sessionId;
};

const parseDataset = (root) => ({
    lang: root.dataset.language || 'zh',
    endpoint: root.dataset.endpoint || '/chat',
    startersEndpoint: root.dataset.startersEndpoint || '/show',
    avatar: root.dataset.avatar || '/static/upload/ai.png',
    title: root.dataset.title || 'AI Assistant',
    assistantName: root.dataset.assistantName || 'EGmicro AI Assistant',
    welcome: root.dataset.welcome || '',
    placeholder: root.dataset.placeholder || '',
    promptTitle: root.dataset.promptTitle || '',
    disclaimer: root.dataset.disclaimer || '',
    newChat: root.dataset.newChat || '',
    copy: root.dataset.copy || '',
    copied: root.dataset.copied || '',
    error: root.dataset.error || '',
    retry: root.dataset.retry || '',
    online: root.dataset.online || '',
});

const starterIcon = (question, index) => {
    const text = String(question || '').trim().toLowerCase();

    if (/电源|驱动|功率|power|driver/.test(text)) return <ThunderboltOutlined/>;
    if (/参数|规格|性能|parameter|spec/.test(text)) return <ControlOutlined/>;
    if (/型号|选型|查找|search|model|selection/.test(text)) return <SearchOutlined/>;
    if (/应用|方案|application|solution/.test(text)) return <DeploymentUnitOutlined/>;
    if (/产品|种类|公司|product|company/.test(text)) return <ProductOutlined/>;

    const fallbacks = [
        <ProductOutlined/>,
        <ThunderboltOutlined/>,
        <ControlOutlined/>,
        <DeploymentUnitOutlined/>,
        <SearchOutlined/>,
        <QuestionCircleOutlined/>,
    ];
    return fallbacks[index % fallbacks.length];
};

marked.setOptions({
    breaks: true,
    gfm: true,
});

marked.use({
    renderer: {
        table(token) {
            const table = marked.Renderer.prototype.table.call(this, token);
            const minWidth = Math.max(280, token.header.length * 108);
            return `<div class="egx-table-scroll" tabindex="0" role="region" aria-label="数据表格，可左右滚动 / Scrollable data table" style="--egx-table-min-width: ${minWidth}px">${table}</div>`;
        },
    },
});

const MarkdownContent = ({content}) => {
    const html = useMemo(
        () => DOMPurify.sanitize(marked.parse(content || '')),
        [content],
    );
    return <div className="egx-markdown" dangerouslySetInnerHTML={{__html: html}}/>;
};

const readAliyunStream = async (response, onText) => {
    if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
    }
    if (!response.body) {
        throw new Error('Streaming is not supported by this browser.');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder('utf-8');
    let buffer = '';

    const consumeBlock = (block) => {
        const data = block
            .split(/\r?\n/)
            .filter((line) => line.startsWith('data:'))
            .map((line) => line.slice(5).trim())
            .join('\n');

        if (!data) return;
        const payload = JSON.parse(data);
        const chunk = payload?.output?.text;
        if (typeof chunk === 'string' && chunk) onText(chunk);
    };

    while (true) {
        const {done, value} = await reader.read();
        buffer += decoder.decode(value || new Uint8Array(), {stream: !done});

        const blocks = buffer.split(/\r?\n\r?\n/);
        buffer = blocks.pop() || '';
        blocks.forEach(consumeBlock);

        if (done) break;
    }

    if (buffer.trim()) consumeBlock(buffer);
};

const ChatWidget = ({config}) => {
    const {message: messageApi} = App.useApp();
    const previewOpen = new URLSearchParams(window.location.search).get('chat') === 'open';
    const [open, setOpen] = useState(previewOpen);
    const [minimized, setMinimized] = useState(false);
    const [messages, setMessages] = useState([]);
    const [starters, setStarters] = useState([]);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [panelSize, setPanelSize] = useState(null);
    const abortRef = useRef(null);
    const listRef = useRef(null);
    const resizeCleanupRef = useRef(null);
    const isEnglish = config.lang.toLowerCase().startsWith('en');

    useEffect(() => () => resizeCleanupRef.current?.(), []);

    const beginResize = useCallback((event, direction) => {
        if (window.innerWidth <= 768) return;

        const panel = event.currentTarget.closest('.egx-panel');
        if (!panel) return;

        event.preventDefault();
        const startRect = panel.getBoundingClientRect();
        const startX = event.clientX;
        const startY = event.clientY;
        const minWidth = 420;
        const minHeight = 480;

        const stop = () => {
            window.removeEventListener('pointermove', move);
            window.removeEventListener('pointerup', stop);
            window.removeEventListener('pointercancel', stop);
            document.body.classList.remove('egx-is-resizing');
            document.body.classList.remove(`egx-is-resizing-${direction}`);
            resizeCleanupRef.current = null;
        };

        const move = (pointerEvent) => {
            const maxWidth = Math.max(minWidth, window.innerWidth - 16);
            const maxHeight = Math.max(minHeight, window.innerHeight);
            const nextWidth = direction === 'top'
                ? startRect.width
                : Math.min(maxWidth, Math.max(minWidth, startRect.width + startX - pointerEvent.clientX));
            const nextHeight = direction === 'left'
                ? startRect.height
                : Math.min(maxHeight, Math.max(minHeight, startRect.height + startY - pointerEvent.clientY));
            setPanelSize({width: nextWidth, height: nextHeight});
        };

        resizeCleanupRef.current?.();
        resizeCleanupRef.current = stop;
        document.body.classList.add('egx-is-resizing');
        document.body.classList.add(`egx-is-resizing-${direction}`);
        window.addEventListener('pointermove', move);
        window.addEventListener('pointerup', stop);
        window.addEventListener('pointercancel', stop);
    }, []);

    useEffect(() => {
        fetch(`${config.startersEndpoint}?lang=${encodeURIComponent(config.lang)}`)
            .then((response) => response.ok ? response.json() : Promise.reject(response))
            .then((payload) => {
                const items = Array.isArray(payload?.data) ? payload.data : [];
                setStarters(items.map((item, index) => {
                    const question = String(item.question || '').trim();
                    return {
                        key: String(item.id ?? index),
                        label: question,
                        icon: starterIcon(question, index),
                    };
                }));
            })
            .catch(() => setStarters([]));
    }, [config.lang, config.startersEndpoint]);

    useEffect(() => {
        if (!open || minimized) return;
        window.setTimeout(() => {
            listRef.current?.scrollTo?.({top: 'bottom', behavior: 'smooth'});
        }, 30);
    }, [messages, open, minimized]);

    const updateAssistant = useCallback((id, patch) => {
        setMessages((current) => current.map((item) => (
            item.id === id ? {...item, ...patch} : item
        )));
    }, []);

    const send = useCallback(async (rawText) => {
        const text = rawText.trim();
        if (!text || loading) return;

        const now = Date.now();
        const userMessage = {id: `user-${now}`, role: 'user', content: text};
        const assistantId = `assistant-${now}`;
        setMessages((current) => [
            ...current,
            userMessage,
            {id: assistantId, role: 'assistant', content: '', loading: true},
        ]);
        setInput('');
        setLoading(true);

        const controller = new AbortController();
        abortRef.current = controller;
        let fullText = '';

        try {
            const response = await fetch(config.endpoint, {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({
                    prompt: {
                        prompt: text,
                        session_id: getSessionId(),
                    },
                }),
                signal: controller.signal,
            });

            await readAliyunStream(response, (chunk) => {
                fullText += chunk;
                updateAssistant(assistantId, {content: fullText, loading: false});
            });

            if (!fullText) throw new Error('Empty response');
            updateAssistant(assistantId, {content: fullText, loading: false});
        } catch (error) {
            if (error.name === 'AbortError') {
                updateAssistant(assistantId, {
                    content: fullText || (isEnglish ? 'Response stopped.' : '已停止生成。'),
                    loading: false,
                });
            } else {
                console.error('EGmicro chat request failed:', error);
                updateAssistant(assistantId, {
                    content: config.error || (isEnglish
                        ? 'The service is temporarily unavailable. Please try again.'
                        : '服务暂时不可用，请稍后重试。'),
                    loading: false,
                    error: true,
                });
            }
        } finally {
            setLoading(false);
            abortRef.current = null;
        }
    }, [config.endpoint, config.error, isEnglish, loading, updateAssistant]);

    const resetConversation = () => {
        abortRef.current?.abort();
        localStorage.removeItem('session_id');
        setMessages([]);
        setInput('');
        setLoading(false);
    };

    const copyMessage = async (content) => {
        try {
            await navigator.clipboard.writeText(content);
            messageApi.success(config.copied || (isEnglish ? 'Copied' : '已复制'));
        } catch {
            messageApi.error(isEnglish ? 'Copy failed' : '复制失败');
        }
    };

    const bubbleItems = messages.map((item) => ({
        key: item.id,
        role: item.role === 'assistant' ? 'ai' : 'user',
        content: item.content,
        loading: item.loading,
        streaming: item.role === 'assistant' && loading && !item.error,
        footer: item.role === 'assistant' && item.content && !item.loading
            ? () => (
                <Tooltip title={config.copy || (isEnglish ? 'Copy' : '复制')} zIndex={10050}>
                    <Button
                        type="text"
                        size="small"
                        className="egx-message-action"
                        icon={<CopyOutlined/>}
                        onClick={() => copyMessage(item.content)}
                        aria-label={config.copy || (isEnglish ? 'Copy' : '复制')}
                    />
                </Tooltip>
            )
            : null,
    }));

    const headerActions = (
        <div className="egx-header-actions">
            <Tooltip title={config.newChat || (isEnglish ? 'New conversation' : '新对话')} zIndex={10050}>
                <Button
                    type="text"
                    icon={<DeleteOutlined/>}
                    onClick={resetConversation}
                    aria-label={config.newChat || (isEnglish ? 'New conversation' : '新对话')}
                />
            </Tooltip>
            <Button
                type="text"
                icon={<MinusOutlined/>}
                onClick={() => setMinimized(true)}
                aria-label={isEnglish ? 'Minimize' : '最小化'}
            />
            <Button
                type="text"
                icon={<CloseOutlined/>}
                onClick={() => setOpen(false)}
                aria-label={isEnglish ? 'Close' : '关闭'}
            />
        </div>
    );

    return (
        <>
            {open && (
                <section
                    className={`egx-panel ${minimized ? 'is-minimized' : ''}`}
                    style={panelSize ? {width: panelSize.width, height: panelSize.height} : undefined}
                    aria-label={config.title}
                >
                    <div className="egx-resize-handle egx-resize-top" onPointerDown={(event) => beginResize(event, 'top')}/>
                    <div
                        className="egx-resize-handle egx-resize-corner"
                        onPointerDown={(event) => beginResize(event, 'corner')}
                        role="separator"
                        aria-label={isEnglish ? 'Drag diagonally to resize the chat window' : '斜向拖拽调整对话窗口大小'}
                        title={isEnglish ? 'Drag diagonally to resize' : '斜向拖拽调整大小'}
                    />
                    <div
                        className="egx-resize-handle egx-resize-left"
                        onPointerDown={(event) => beginResize(event, 'left')}
                        role="separator"
                        aria-orientation="vertical"
                        aria-label={isEnglish ? 'Drag to resize the chat window' : '拖拽调整对话窗口宽度'}
                        title={isEnglish ? 'Drag to resize' : '拖拽调整宽度'}
                    >
                        <ColumnWidthOutlined aria-hidden="true"/>
                    </div>
                    <header className="egx-header">
                        <div className="egx-brand">
                            <Avatar size={42} src={config.avatar} icon={<CommentOutlined/>}/>
                            <div>
                                <strong>{config.title}</strong>
                                <span><i/>{config.online || (isEnglish ? 'Online' : '在线')}</span>
                            </div>
                        </div>
                        {headerActions}
                    </header>

                    {!minimized && (
                        <>
                            <main className="egx-body">
                                {messages.length === 0 ? (
                                    <div className="egx-empty">
                                        {starters.length > 0 && (
                                            <div className="egx-prompt-section">
                                                <div className="egx-prompt-heading">
                                                    <BulbOutlined/>
                                                    <span>{config.promptTitle}</span>
                                                </div>
                                                <Prompts
                                                    items={starters}
                                                    vertical
                                                    onItemClick={({data}) => send(String(data.label || ''))}
                                                />
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <Bubble.List
                                        ref={listRef}
                                        autoScroll
                                        items={bubbleItems}
                                        role={{
                                            ai: {
                                                placement: 'start',
                                                variant: 'borderless',
                                                avatar: <Avatar size={32} src={config.avatar}/>,
                                                contentRender: (content) => <MarkdownContent content={content}/>,
                                            },
                                            user: {
                                                placement: 'end',
                                                variant: 'filled',
                                                shape: 'corner',
                                            },
                                        }}
                                    />
                                )}
                            </main>
                            <footer className="egx-composer">
                                <Sender
                                    value={input}
                                    onChange={setInput}
                                    onSubmit={send}
                                    onCancel={() => abortRef.current?.abort()}
                                    loading={loading}
                                    placeholder={config.placeholder}
                                    autoSize={{minRows: 1, maxRows: 4}}
                                />
                                <div className="egx-disclaimer">
                                    <SafetyCertificateOutlined/> {config.disclaimer}
                                </div>
                            </footer>
                        </>
                    )}
                </section>
            )}

            {(!open || minimized) && (
                <button
                    className="egx-launcher"
                    onClick={() => {
                        setOpen(true);
                        setMinimized(false);
                    }}
                    aria-label={config.title}
                    aria-expanded={false}
                >
                    <Avatar className="egx-launcher-avatar" src={config.avatar} icon={<CommentOutlined/>}/>
                    <span className="egx-launcher-pulse"/>
                </button>
            )}
        </>
    );
};

const mount = document.getElementById('egmicro-ant-chat-root');

if (mount) {
    const config = parseDataset(mount);
    createRoot(mount).render(
        <ConfigProvider
            theme={{
                token: {
                    colorPrimary: '#4f46e5',
                    borderRadius: 12,
                    fontFamily: 'Inter, "PingFang SC", "Microsoft YaHei", sans-serif',
                },
            }}
        >
            <App>
                <ChatWidget config={config}/>
            </App>
        </ConfigProvider>,
    );
}
